import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useSuperAdminList } from '../hooks';
import {
  useTodayOrders,
  usePendingOrders,
  useTodayRevenue,
  useOrdersStatistics,
} from '@features/orders/hooks';
import { formatCurrency } from '@features/orders/utils';
import './DashboardPage.scss';

export function SuperAdminDashboardPage() {
  const { data: superAdmins = [], isLoading: isLoadingAdmins } = useSuperAdminList();
  const { data: todayOrders = [], isLoading: isLoadingTodayOrders } = useTodayOrders();
  const { data: pendingOrders = [], isLoading: isLoadingPendingOrders } = usePendingOrders();
  const { data: todayRevenue = 0, isLoading: isLoadingRevenue } = useTodayRevenue();

  // Tính toán 7 ngày gần nhất cho biểu đồ
  const last7Days = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  }, []);

  const dateFrom = last7Days[0];
  const dateTo = last7Days[last7Days.length - 1];
  const { data: statistics, isLoading: isLoadingStats } = useOrdersStatistics(dateFrom, dateTo);

  const summary = useMemo(() => {
    const list = superAdmins;
    return {
      total: list.length,
      active: list.filter((x) => x.status === 'active').length,
      inactive: list.filter((x) => x.status !== 'active').length,
      verified: list.filter((x) => x.verified).length,
    };
  }, [superAdmins]);

  // Chuẩn bị dữ liệu cho biểu đồ đơn hàng theo ngày
  const ordersChartData = useMemo(() => {
    if (!statistics?.ordersByDate) {
      return {
        categories: last7Days.map((d) => {
          const date = new Date(d);
          return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
        }),
        series: [0, 0, 0, 0, 0, 0, 0],
      };
    }

    const dataMap = new Map(statistics.ordersByDate.map((item) => [item.date, item.count]));
    const series = last7Days.map((date) => dataMap.get(date) || 0);
    const categories = last7Days.map((d) => {
      const date = new Date(d);
      return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
    });

    return { categories, series };
  }, [statistics, last7Days]);

  // Chuẩn bị dữ liệu cho biểu đồ tròn - phân bổ trạng thái
  const statusChartData = useMemo(() => {
    if (!statistics?.statusDistribution) {
      return { labels: [], series: [] };
    }

    const statusLabels: Record<string, string> = {
      pending: 'Chờ thanh toán',
      processing: 'Đang xử lý',
      completed: 'Hoàn thành',
      cancelled: 'Đã hủy',
    };

    const labels = statistics.statusDistribution.map((item) => statusLabels[item.status] || item.status);
    const series = statistics.statusDistribution.map((item) => item.count);

    return { labels, series };
  }, [statistics]);

  // Chuẩn bị dữ liệu cho biểu đồ doanh thu theo ngày
  const revenueChartData = useMemo(() => {
    if (!statistics?.ordersByDate) {
      return {
        categories: last7Days.map((d) => {
          const date = new Date(d);
          return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
        }),
        series: [0, 0, 0, 0, 0, 0, 0],
      };
    }

    const dataMap = new Map(statistics.ordersByDate.map((item) => [item.date, item.revenue]));
    const series = last7Days.map((date) => dataMap.get(date) || 0);
    const categories = last7Days.map((d) => {
      const date = new Date(d);
      return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
    });

    return { categories, series };
  }, [statistics, last7Days]);

  // Cấu hình biểu đồ cột - Đơn hàng theo ngày
  const ordersChartOptions = {
    chart: {
      type: 'bar' as const,
      toolbar: { show: false },
      height: 350,
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '60%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: ordersChartData.categories,
    },
    yaxis: {
      title: {
        text: 'Số lượng đơn hàng',
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: ['#7367f0'],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    colors: ['#7367f0'],
    tooltip: {
      y: {
        formatter: (val: number) => `${val} đơn hàng`,
      },
    },
  };

  // Cấu hình biểu đồ tròn - Phân bổ trạng thái
  const statusChartOptions = {
    chart: {
      type: 'donut' as const,
      toolbar: { show: false },
      height: 350,
    },
    labels: statusChartData.labels,
    colors: ['#ffa726', '#42a5f5', '#66bb6a', '#ef5350'],
    legend: {
      position: 'bottom' as const,
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} đơn hàng`,
      },
    },
  };

  // Cấu hình biểu đồ đường - Doanh thu theo ngày
  const revenueChartOptions = {
    chart: {
      type: 'line' as const,
      toolbar: { show: false },
      height: 350,
    },
    stroke: {
      curve: 'smooth' as const,
      width: 3,
    },
    xaxis: {
      categories: revenueChartData.categories,
    },
    yaxis: {
      title: {
        text: 'Doanh thu (VND)',
      },
      labels: {
        formatter: (val: number) => {
          if (val >= 1000000) {
            return `${(val / 1000000).toFixed(1)}M`;
          }
          if (val >= 1000) {
            return `${(val / 1000).toFixed(0)}K`;
          }
          return val.toString();
        },
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: ['#66bb6a'],
        inverseColors: false,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    colors: ['#66bb6a'],
    tooltip: {
      y: {
        formatter: (val: number) => formatCurrency(val),
      },
    },
  };

  return (
    <div className="dashboard-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Bảng điều khiển Super Admin</h2>
          <p className="text-muted mb-0">Theo dõi trạng thái quản trị viên và thống kê đơn hàng.</p>
        </div>
      </div>

      {/* Super Admin Summary Cards */}
      <div className="cards-grid cards-grid--three mb-4">
        <div className="stat-card">
          <span className="title">Tổng Tài Khoản</span>
          <span className="value">{isLoadingAdmins ? '...' : summary.total}</span>
          <span className="chip">Tổng quyền hạn</span>
        </div>
        <div className="stat-card">
          <span className="title">Đang hoạt động</span>
          <span className="value text-success">{isLoadingAdmins ? '...' : summary.active}</span>
          <span className="chip">Active</span>
        </div>
        <div className="stat-card">
          <span className="title">Vô hiệu hóa</span>
          <span className="value text-danger">{isLoadingAdmins ? '...' : summary.inactive}</span>
          <span className="chip">Inactive</span>
        </div>
      </div>

      {/* Orders Summary Cards */}
      <div className="cards-grid mb-4">
        <div className="stat-card stat-card--orders">
          <span className="title">Đơn hàng hôm nay</span>
          <span className="value text-info">
            {isLoadingTodayOrders ? '...' : todayOrders.length}
          </span>
          <span className="chip">Hôm nay</span>
        </div>
        <div className="stat-card stat-card--orders">
          <span className="title">Chờ thanh toán</span>
          <span className="value text-warning">
            {isLoadingPendingOrders ? '...' : pendingOrders.length}
          </span>
          <span className="chip">Pending</span>
        </div>
        <div className="stat-card stat-card--orders">
          <span className="title">Doanh thu hôm nay</span>
          <span className="value text-success">
            {isLoadingRevenue ? '...' : formatCurrency(todayRevenue)}
          </span>
          <span className="chip">Hôm nay</span>
        </div>
        <div className="stat-card stat-card--orders">
          <span className="title">Tổng đơn hàng</span>
          <span className="value text-primary">
            {isLoadingStats ? '...' : statistics?.statusDistribution.reduce((sum, item) => sum + item.count, 0) || 0}
          </span>
          <span className="chip">Tất cả</span>
        </div>
      </div>

      {/* Charts Row 1: Orders by Date & Status Distribution */}
      <div className="charts-row mb-4">
        <div className="chart-card">
          <div className="card-header">
            <h5 className="card-title">Đơn hàng theo ngày (7 ngày gần nhất)</h5>
          </div>
          <div className="card-body">
            {isLoadingStats ? (
              <div className="text-center py-5">Đang tải dữ liệu...</div>
            ) : (
              <Chart
                options={ordersChartOptions}
                series={[{ name: 'Đơn hàng', data: ordersChartData.series }]}
                type="bar"
                height={350}
              />
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="card-header">
            <h5 className="card-title">Phân bổ trạng thái đơn hàng</h5>
          </div>
          <div className="card-body">
            {isLoadingStats ? (
              <div className="text-center py-5">Đang tải dữ liệu...</div>
            ) : (
              <Chart
                options={statusChartOptions}
                series={statusChartData.series}
                type="donut"
                height={350}
              />
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2: Revenue by Date */}
      <div className="charts-row mb-4">
        <div className="chart-card chart-card--full">
          <div className="card-header">
            <h5 className="card-title">Doanh thu theo ngày (7 ngày gần nhất)</h5>
          </div>
          <div className="card-body">
            {isLoadingStats ? (
              <div className="text-center py-5">Đang tải dữ liệu...</div>
            ) : (
              <Chart
                options={revenueChartOptions}
                series={[{ name: 'Doanh thu', data: revenueChartData.series }]}
                type="line"
                height={350}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
